import React from 'react';
import { motion } from 'framer-motion';
import { Calculator, Car, Fuel, Wrench } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import SectionTitle from './ui/SectionTitle';
import { stagger, scaleIn } from '../lib/animations';

interface Feature {
  icon: React.ComponentType<LucideProps>;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
  details: string[];
}

const features: Feature[] = [
  {
    icon: Car,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-50',
    title: 'Garagem',
    description:
      'Cadastre seus veículos com todos os detalhes para um controle preciso de consumo e custos.',
    details: [
      'Consumo médio por combustível (km/L)',
      'Capacidade do tanque e odômetro',
      'Suporte a múltiplos veículos',
    ],
  },
  {
    icon: Fuel,
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-50',
    title: 'Abastecimentos',
    description:
      'Registro completo de cada abastecimento com relatórios mensais e anuais detalhados.',
    details: [
      'Litros, preço por litro e posto',
      'Foto do comprovante fiscal',
      'Relatório mensal e anual',
    ],
  },
  {
    icon: Wrench,
    iconColor: 'text-violet-600',
    iconBg: 'bg-violet-50',
    title: 'Manutenções',
    description:
      'Acompanhe revisões preventivas e corretivas com lembretes para nunca perder um prazo.',
    details: [
      'Histórico de serviços completo',
      'Alertas e notificações por km',
      'Revisões preventivas e corretivas',
    ],
  },
  {
    icon: Calculator,
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-50',
    title: 'Calculadora',
    description:
      'Descubra em segundos qual combustível é mais vantajoso com base no consumo real do seu carro.',
    details: [
      'Comparativo etanol × gasolina em %',
      'Estimativa de custo por distância',
      'Consumo médio real do veículo',
    ],
  },
];

// Card individual de funcionalidade com efeito hover de elevação
const FeatureCard: React.FC<Feature> = ({
  icon: Icon,
  iconColor,
  iconBg,
  title,
  description,
  details,
}) => (
  <motion.article
    variants={scaleIn}
    className="group relative cursor-default rounded-3xl border border-gray-100 bg-white p-7 shadow-sm
      transition-all duration-300 hover:-translate-y-1.5 hover:border-amber-200 hover:shadow-xl"
  >
    {/* Ícone */}
    <div className={`mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl ${iconBg}`}>
      <Icon className={`h-7 w-7 ${iconColor}`} strokeWidth={1.5} />
    </div>

    <h3 className="mb-2 text-lg font-bold text-gray-900">{title}</h3>
    <p className="mb-4 text-sm leading-relaxed text-gray-500">{description}</p>

    <ul className="space-y-2">
      {details.map((d) => (
        <li key={d} className="flex items-center gap-2 text-xs text-gray-500">
          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400" />
          {d}
        </li>
      ))}
    </ul>

    {/* Linha de destaque no hover */}
    <div className="absolute bottom-0 left-6 right-6 h-0.5 origin-left scale-x-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 transition-transform duration-300 group-hover:scale-x-100" />
  </motion.article>
);

// Seção de funcionalidades com grid responsivo
const FeaturesSection: React.FC = () => (
  <section id="funcionalidades" className="bg-gray-50 py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionTitle
        eyebrow="Funcionalidades"
        title="Tudo que você precisa para controlar seu veículo"
        subtitle="Do cadastro da garagem ao comparativo de combustível, o Gas2 organiza toda a rotina automotiva em uma interface direta e moderna."
      />
      <motion.div
        className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        {features.map((f) => (
          <FeatureCard key={f.title} {...f} />
        ))}
      </motion.div>
    </div>
  </section>
);

export default FeaturesSection;

